import type { ComponentPropsWithoutRef, ReactNode } from "react";

export function cn(...classes: (string | false | null | undefined)[]): string {
  return classes.filter(Boolean).join(" ");
}

// ---------------------------------------------------------------------------
// Surfaces
// ---------------------------------------------------------------------------

export function Panel({
  children,
  className,
  ...rest
}: ComponentPropsWithoutRef<"section">) {
  return (
    <section
      {...rest}
      className={cn("rounded-lg border border-line bg-surface", className)}
    >
      {children}
    </section>
  );
}

export function PanelHeader({
  title,
  action,
  description,
}: {
  title: string;
  action?: ReactNode;
  description?: string;
}) {
  return (
    <header className="flex items-start justify-between gap-4 border-b border-line px-4 py-3">
      <div>
        <h2 className="eyebrow">{title}</h2>
        {description ? <p className="mt-1 text-xs text-ink-muted">{description}</p> : null}
      </div>
      {action}
    </header>
  );
}

// ---------------------------------------------------------------------------
// Badges
// ---------------------------------------------------------------------------

export type Tone = "neutral" | "accent" | "positive" | "negative" | "caution";

const TONE_CLASS: Record<Tone, string> = {
  neutral: "bg-surface-sunken text-ink-muted border-line",
  accent: "bg-accent-soft text-accent border-accent-line",
  positive: "bg-positive-soft text-positive border-positive/20",
  negative: "bg-negative-soft text-negative border-negative/20",
  caution: "bg-caution-soft text-caution border-caution/20",
};

export function Badge({
  children,
  tone = "neutral",
  className,
}: {
  children: ReactNode;
  tone?: Tone;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[0.6875rem] font-medium whitespace-nowrap",
        TONE_CLASS[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Buttons
// ---------------------------------------------------------------------------

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";

const BUTTON_CLASS: Record<ButtonVariant, string> = {
  primary: "bg-accent text-white border-accent hover:bg-accent/90",
  secondary: "bg-surface text-ink border-line-strong hover:bg-surface-sunken",
  ghost: "bg-transparent text-ink-muted border-transparent hover:bg-surface-sunken hover:text-ink",
  danger: "bg-surface text-negative border-negative/30 hover:bg-negative-soft",
};

export function Button({
  variant = "secondary",
  className,
  ...rest
}: ComponentPropsWithoutRef<"button"> & { variant?: ButtonVariant }) {
  return (
    <button
      {...rest}
      className={cn(
        "inline-flex items-center justify-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors",
        "disabled:cursor-not-allowed disabled:opacity-50",
        BUTTON_CLASS[variant],
        className,
      )}
    />
  );
}

// ---------------------------------------------------------------------------
// Figures
// ---------------------------------------------------------------------------

/** A labelled figure. The unit of the metric strips across the workspace. */
export function Figure({
  label,
  value,
  hint,
  action,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="px-4 py-3">
      <div className="eyebrow">{label}</div>
      <div className="numeric mt-1 flex items-baseline gap-2 text-lg font-semibold text-ink">
        {value}
        {action}
      </div>
      {hint ? <div className="mt-0.5 text-xs text-ink-muted">{hint}</div> : null}
    </div>
  );
}

export function EmptyState({ title, detail }: { title: string; detail?: string }) {
  return (
    <div className="px-4 py-10 text-center">
      <p className="text-sm font-medium text-ink">{title}</p>
      {detail ? <p className="mx-auto mt-1 max-w-md text-xs text-ink-muted">{detail}</p> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Semantic marks — ✓ and ⚠ carry meaning in this product, so they are typed.
// ---------------------------------------------------------------------------

export function Mark({ kind }: { kind: "positive" | "negative" | "caution" }) {
  const glyph = kind === "positive" ? "✓" : "⚠";
  const tone =
    kind === "positive" ? "text-positive" : kind === "negative" ? "text-negative" : "text-caution";
  return (
    <span aria-hidden className={cn("shrink-0 select-none", tone)}>
      {glyph}
    </span>
  );
}
